/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { html, View } from 'rune-ts';
import klass from './GnbCreatorShop24MoView.module.scss';
import {
  ArrowLeftIcon,
  CartFillIcon,
  MarppleShopLogo,
  SearchIcon,
} from '@services/MarppleShop/renderApp/components/atoms/Icon';
import {
  CreatorLogoSearch,
  CreatorSearchStatusChange,
} from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShop/views/CreatorLogoSearch.ts';
import {
  SearchModuleCreatorShop,
  SearchModuleCreatorShopCloseEvent,
} from '@services/MarppleShop/renderApp/components/cells/SearchModule/SearchModuleCreatorShop.ts';
import { getSearchInputPlaceholder } from '@services/MarppleShop/features/Search/util';
import { classes } from '@services/MarppleShop/shared/util';
import { throttleAnimationFrameLevel } from '@modules/Util/F/Function/animation';
import { makeMainUrl } from '@services/MarppleShop/shared/util/url.ts';
import { dataStr } from '@services/MarppleShop/shared/util/dataStr.ts';
import {
  makeCartNavigate,
  makeHomeScreenNavigate,
  PostMessageNavigate,
} from '@services/MarppleShop/shared/app/navigate.ts';
import { Modal } from '@services/MarppleShop/renderApp/components/atoms/Modal/Modal.ts';
import { CreatorLogo } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShop/views/CreatorLogo.ts';
import { MShopUtilF } from '@modules/MShop/Util/F/Function/module/MShopUtilF';
import { mpsPostMessage } from '@services/MarppleShop/shared/app/util.ts';
import { UtilS } from '@modules/Util/S/Function/module/UtilS';
import { FROM_MAIN_REGEXP } from '@services/MarppleShop/renderApp/components/cells/Header/libs/contants/regex.ts';
import { CartCountView } from '@services/MarppleShop/renderApp/components/atoms/Icon/Icons.ts';

const SCROLL_Y_THRESHOLD_PX = 10;

type TTheme = 'Dark' | 'Light' | 'Logo';

const classesByTheme: Record<TTheme, string> = {
  Logo: klass.logo_theme,
  Dark: klass.dark_theme,
  Light: '',
};

export type TGnbCreatorShop24MoViewProps = {
  theme: TTheme;
  cart_count: number;
  creator: {
    id: number;
    profile_url: string | null;
    name: string | null;
    domain_name: string;
    link: string;
  };
  search:
    | {
        value?: string;
        placeholder?: string;
      }
    | undefined;
  plus:
    | {
        domain_name: string;
        logo_url: string | null;
        name: string;
        link?: string;
        navigate?: PostMessageNavigate;
      }
    | undefined;
};

/**
 * https://www.figma.com/design/Top00pi5q5ylSxEviOOciV/MarppleShop-Design-System?node-id=22984-28742&m=dev
 *
 * @see HeaderCreatorShop
 */
export class GnbCreatorShop24MoView extends View<TGnbCreatorShop24MoViewProps> {
  private initial_theme: TGnbCreatorShop24MoViewProps['theme'];

  private creator_logo_view = new CreatorLogo({
    plus: this.data.plus,
    creator: this.data.creator,
    is_mobile: true,
    theme: 'bright',
  });

  private creator_logo_search_view = new CreatorLogoSearch({ profile_url: this.data.creator.profile_url });

  private search_module_creator_shop_view = new SearchModuleCreatorShop(this.data.search ?? {}, {
    is_mobile: true,
    highlight: 'stroke',
    without_visible_interaction: true,
    search_input_left_part: this.creator_logo_search_view,
    search_type: 'CREATOR_SHOP',
    store_id: this.data.creator.id,
    store_domain_name: this.data.creator.domain_name,
  });

  private search_modal_view = new Modal({
    size: 'large',
    body: this.search_module_creator_shop_view,
    isOpen: false,
    full_width: true,
  });

  constructor(props: TGnbCreatorShop24MoViewProps) {
    super(props);
    this.initial_theme = props.theme;
  }

  setTheme(theme: TTheme) {
    this.data.theme = theme;

    const element = this.element();

    const theme_classes = Object.entries(classesByTheme)
      .map(([, v]) => v)
      .filter(Boolean);
    theme_classes.forEach((v) => element.classList.remove(v));
    theme_classes.map((v) => element.querySelectorAll(`.${v}`).forEach((el) => el.classList.remove(v)));

    const logo_wrapper_element = element.querySelector('.logo_wrapper')!;
    if (theme === 'Logo') {
      logo_wrapper_element.innerHTML = this.creator_logo_view.toHtml();
    } else {
      logo_wrapper_element.innerHTML = html`<a
        href="${makeMainUrl(`/${ET.lang}`)}"
        data-post-message="${dataStr(makeHomeScreenNavigate())}"
        class="${classes(klass.primary_initial_icon, classesByTheme[theme])}"
        >${MarppleShopLogo}</a
      >`.make(this);
    }

    if (classesByTheme[theme]) {
      element.classList.add(classesByTheme[theme]);
      element.querySelector(`.${klass.header_left}`)!.classList.add(classesByTheme[theme]);
      element.querySelector(`.${klass.arrow_icon}`)!.classList.add(classesByTheme[theme]);
      element.querySelector(`.${klass.primary_initial_icon}`)?.classList.add(classesByTheme[theme]);
      element.querySelector(`.${klass.search_icon}`)!.classList.add(classesByTheme[theme]);
      element.querySelector(`.${klass.cart_icon}`)!.classList.add(classesByTheme[theme]);
    }
  }

  /**
   * @see https://github.com/marpple/marpple-web/blob/65fc3cf25a5cdbef49f74c0a03660325aaa53f92/services/MarppleShop/renderApp/components/cells/Header/Base/HeaderAbstract.ts#L168-L187
   */
  setCartCount(cart_count: number) {
    this.data.cart_count = cart_count;

    const cartCountView = this.subView(CartCountView);

    if (!this.data.cart_count) {
      cartCountView?.element().remove();
      return this;
    }

    if (cartCountView) {
      cartCountView.setCount(this.data.cart_count).redraw();
    } else {
      this.element()
        .querySelector('.cart-fill-icon')
        ?.append(new CartCountView({ count: this.data.cart_count }).render());
    }
  }

  protected override onRender() {
    this.delegate('click', `.${klass.arrow_icon}`, () => {
      if (MShopUtilF.isApp()) {
        mpsPostMessage({ goBack: true });
      } else {
        if (UtilS.isNboxApp()) {
          window.history.back();
        } else {
          if (
            FROM_MAIN_REGEXP[
              (process.env.NODE_ENV as 'staging' | 'production' | 'development') || 'development'
            ].test(document.referrer)
          ) {
            window.history.back();
          } else {
            if (box.sel('store_url')) {
              window.location.href = box.sel('store_url') + (box.sel('is_studio') ? '/settings/styles' : '');
            } else {
              window.location.href = `/${T.lang}`;
            }
          }
        }
      }
    });

    this.delegate('click', `.${klass.search_icon}`, () => {
      this.search_modal_view.open();
    });

    this.delegate(SearchModuleCreatorShopCloseEvent, SearchModuleCreatorShop, () => {
      this.search_modal_view.close();
    });

    /**
     * @see https://github.com/marpple/marpple-web/blob/b73596b5a73537b9d0dfa0fa94d3e4a7e11e8472/services/MarppleShop/renderApp/components/cells/Header/CreatorShop/HeaderCreatorShop.ts#L313-L326
     */
    this.delegate(CreatorSearchStatusChange, CreatorLogoSearch, (event) => {
      this.search_module_creator_shop_view.changeSearchType(
        event.detail.status === 'enable' ? 'CREATOR_SHOP' : 'ALL',
      );
      this.creator_logo_search_view.changeStatus(event.detail.status);

      this.search_module_creator_shop_view.InputSearchView.data.placeholder = getSearchInputPlaceholder(
        ET.lang,
      );
      this.search_module_creator_shop_view.InputSearchView.redraw();
    });

    window.addEventListener(
      'scroll',
      throttleAnimationFrameLevel(() => {
        if (
          document.body.classList.contains('body-fixed') ||
          document.body.classList.contains('fixed') ||
          document.querySelector('html')?.getAttribute('f_index') !== '0'
        ) {
          return;
        }

        const next_theme = window.scrollY > SCROLL_Y_THRESHOLD_PX ? 'Logo' : this.initial_theme;
        if (next_theme !== this.data.theme) {
          this.setTheme(next_theme);
        }
      }) as any,
    );
  }

  protected override onMount() {
    this.setTheme(window.scrollY > SCROLL_Y_THRESHOLD_PX ? 'Logo' : this.initial_theme);
  }

  protected override template({ cart_count, theme }: TGnbCreatorShop24MoViewProps) {
    return html`<nav class="${classes(klass.gnb_creator_shop_24_mo, classesByTheme[theme])}">
      <div class="${classes(klass.header_left, classesByTheme[theme])}">
        <button type="button" class="${classes(klass.arrow_icon, classesByTheme[theme])}">
          ${ArrowLeftIcon}
        </button>

        <div class="logo_wrapper">
          ${theme === 'Logo'
            ? this.creator_logo_view
            : html`<a
                href="${makeMainUrl(`/${ET.lang}`)}"
                data-post-message="${dataStr(makeHomeScreenNavigate())}"
                class="${classes(klass.primary_initial_icon, classesByTheme[theme])}"
                >${MarppleShopLogo}</a
              >`}
        </div>
      </div>

      <div class="${klass.header_right}">
        <button type="button" class="${classes(klass.search_icon, classesByTheme[theme])}">
          ${SearchIcon}
        </button>

        <a
          class="${classes(klass.cart_icon, classesByTheme[theme])}"
          data-post-message="${dataStr(makeCartNavigate())}"
          href="/${ET.lang}/@/cart"
          >${CartFillIcon({ count: cart_count })}</a
        >
      </div>

      ${this.search_modal_view}
    </nav>`;
  }
}
